import type { Facility } from "../types";
import { FacilityId } from "../types/constants";

const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";

/**
 * Calc-side facility catalog. Schema mirrors the upstream game-data dump
 * (`FactoryBuildingTable` + adjacent metadata), with `id` narrowed to the
 * branded `FacilityId` enum. Distinct-item port caps used by the
 * bin-packing solver are derived at consumption time from buffer counts —
 * see `src/types/core.ts` for the mapping.
 *
 * Source-direction facilities (`unloader_1`, `pump_1`, `pump_2`) are
 * referenced by `rawMaterialSources` in `src/data/index.ts`: each raw
 * material is sourced by exactly one of these and contributes its power
 * cost × pickup-point count to the plan total via `aggregateBinTotals`.
 * `loader_1` is present for forward compatibility (solid-disposal sink,
 * Phase 4 work); no recipe references it today.
 */
export const facilities: Facility[] = [
  {
    id: FacilityId.FURNANCE_1,
    numId: 3,
    tier: 1,
    category: 6,
    powerConsumption: 5,
    buffersIn: { belt: [{ ports: 3 }], pipe: [{ ports: 1 }] },
    buffersOut: { belt: [{ ports: 3 }], pipe: [{ ports: 1 }] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.GRINDER_1,
    numId: 11,
    tier: 1,
    category: 6,
    powerConsumption: 5,
    buffersIn: { belt: [{ ports: 3 }], pipe: [] },
    buffersOut: { belt: [{ ports: 3 }], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.MIX_POOL_1,
    numId: 31,
    tier: 3,
    category: 27,
    powerConsumption: 50,
    buffersIn: { belt: [{ ports: 2 }], pipe: [{ ports: 1 }, { ports: 1 }] },
    buffersOut: { belt: [{ ports: 2 }], pipe: [{ ports: 1 }, { ports: 1 }] },
    cacheSlots: 5,
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.WINDER_1,
    numId: 1,
    tier: 1,
    category: 6,
    powerConsumption: 10,
    buffersIn: { belt: [{ ports: 6 }], pipe: [] },
    buffersOut: { belt: [{ ports: 6 }], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.COMPONENT_MC_1,
    numId: 17,
    tier: 2,
    category: 6,
    powerConsumption: 20,
    buffersIn: { belt: [{ ports: 3 }], pipe: [] },
    buffersOut: { belt: [{ ports: 3 }], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.DISMANTLER_1,
    numId: 18,
    tier: 3,
    category: 6,
    powerConsumption: 20,
    buffersIn: { belt: [{ ports: 6 }], pipe: [] },
    buffersOut: { belt: [{ ports: 6 }], pipe: [{ ports: 1 }] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.FILLING_POWDER_MC_1,
    numId: 8,
    tier: 3,
    category: 6,
    powerConsumption: 20,
    buffersIn: { belt: [{ ports: 6 }], pipe: [{ ports: 1 }] },
    buffersOut: { belt: [{ ports: 6 }], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.LIQUID_CLEANER_1,
    numId: 45,
    tier: 3,
    category: 28,
    powerConsumption: 50,
    buffersIn: { belt: [], pipe: [{ ports: 1 }] },
    buffersOut: { belt: [], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.LIQUID_PURIFIER_1,
    numId: 75,
    tier: 4,
    category: 6,
    powerConsumption: 50,
    buffersIn: { belt: [], pipe: [{ ports: 2 }] },
    buffersOut: { belt: [], pipe: [{ ports: 1 }, { ports: 1 }] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.PLANTER_1,
    numId: 5,
    tier: 3,
    category: 6,
    powerConsumption: 20,
    buffersIn: { belt: [{ ports: 5 }], pipe: [{ ports: 1 }] },
    buffersOut: { belt: [{ ports: 5 }], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.LOADER_1,
    numId: 25,
    tier: 3,
    category: 10,
    powerConsumption: 0,
    buffersIn: { belt: [{ ports: 1 }], pipe: [] },
    buffersOut: { belt: [], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.PUMP_2,
    numId: 78,
    tier: 3,
    category: 25,
    powerConsumption: 20,
    buffersIn: { belt: [], pipe: [] },
    buffersOut: { belt: [], pipe: [{ ports: 1 }] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.TOOLS_ASSEBLING_MC_1,
    numId: 19,
    tier: 3,
    category: 6,
    powerConsumption: 20,
    buffersIn: { belt: [{ ports: 6 }], pipe: [] },
    buffersOut: { belt: [{ ports: 6 }], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.MIX_POOL_2,
    numId: 81,
    tier: 3,
    category: 27,
    powerConsumption: 100,
    buffersIn: { belt: [{ ports: 4 }], pipe: [{ ports: 1 }, { ports: 1 }] },
    buffersOut: { belt: [{ ports: 4 }], pipe: [{ ports: 1 }, { ports: 1 }] },
    cacheSlots: 8,
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.SEEDCOLLECTOR_1,
    numId: 29,
    tier: 3,
    category: 6,
    powerConsumption: 10,
    buffersIn: { belt: [{ ports: 5 }], pipe: [] },
    buffersOut: { belt: [{ ports: 5 }], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.PUMP_1,
    numId: 26,
    tier: 2,
    category: 25,
    powerConsumption: 10,
    buffersIn: { belt: [], pipe: [] },
    buffersOut: { belt: [], pipe: [{ ports: 1 }] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.SHAPER_1,
    numId: 24,
    tier: 2,
    category: 6,
    powerConsumption: 10,
    buffersIn: { belt: [{ ports: 3 }], pipe: [] },
    buffersOut: { belt: [{ ports: 3 }], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    id: FacilityId.XIRANITE_OVEN_1,
    numId: 20,
    tier: 4,
    category: 6,
    powerConsumption: 50,
    buffersIn: { belt: [{ ports: 5 }], pipe: [{ ports: 1 }] },
    buffersOut: { belt: [{ ports: 5 }], pipe: [{ ports: 1 }] },
    domains: [2],
    cap: { base: 1, increments: [1, 4, 2] },
  },
  {
    id: FacilityId.THICKENER_1,
    numId: 10,
    tier: 4,
    category: 6,
    powerConsumption: 50,
    buffersIn: { belt: [{ ports: 6 }], pipe: [] },
    buffersOut: { belt: [{ ports: 6 }], pipe: [] },
    domains: [],
    cap: null,
  },
  {
    // Depot Unloader — solid-raw pickup point. Game data: type 11
    // (unloader-class), 0 power, 1 belt output port, no inputs.
    // Throughput: 30/min per facility (matches belt capacity).
    id: FacilityId.UNLOADER_1,
    numId: -1, // entity-ids.json not in this data dump
    tier: 3, // rarity 3 from ItemTable (item_port_unloader_1)
    category: 11,
    powerConsumption: 0,
    buffersIn: { belt: [], pipe: [] },
    buffersOut: { belt: [{ ports: 1 }], pipe: [] },
    domains: [],
    cap: null,
  },
];

facilities.forEach((f) => {
  f.iconUrl = `${basePath}/images/facilities/${f.id}.png`;
});
